/* eslint-disable import/no-named-as-default-member */
import { map } from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService } from '@bingads-webui-react/wizard';
import { loadExtensionsAssociations } from '@bingads-webui-campaign-react/search-campaign-data';
import addressSchema from '@bingads-webui-component-legacy/address/addressSchema';

const extensionSchemaProcessors = {
  Location: entity => ({
    ...entity,
    display: addressSchema.display(entity.Address),
  }),
};

export const useExtensionsRepository = ({ permissions }) => {
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;

  const { state } = useWizardService();
  const {
    campaignId,
    campaignName,
    adExtensionsAssociationsFactory,
  } = state.context.wizardData;

  const availableExtensionTypes = useMemo(
    () => map(adExtensionsAssociationsFactory.getAvailableExtensions(permissions, 'Search'), extension => adExtensionsAssociationsFactory.extensionEntityMapping[extension] || extension),
    [adExtensionsAssociationsFactory, permissions]
  );

  const load = useCallback(() => loadExtensionsAssociations({
    cid,
    aid,
    campaignId,
    adExtensionTypes: availableExtensionTypes,
  }).then(({ value }) => {
    const adExtensionsAssociations = value.reduce((extensionsAssociations, item) => {
      const entityType = item.AdExtension['@odata.type'].split('.').pop(); // Extract the type
      const extensionType = adExtensionsAssociationsFactory.extensionNameMapping[entityType] || entityType;
      if (!extensionsAssociations[extensionType]) {
        // eslint-disable-next-line no-param-reassign
        extensionsAssociations[extensionType] = [];
      }
      if (extensionSchemaProcessors[extensionType]) {
        extensionsAssociations[extensionType].push(extensionSchemaProcessors[extensionType](item.AdExtension));
      } else {
        extensionsAssociations[extensionType].push(item.AdExtension);
      }
      return extensionsAssociations;
    }, {});

    return {
      campaignId,
      campaignName,
      adExtensionsAssociations,
      loaded: true,
    };
  }), [adExtensionsAssociationsFactory.extensionNameMapping, aid, availableExtensionTypes, campaignId, campaignName, cid]);

  const save = useCallback(({ data }) => {
    const { adExtensionsAssociations } = data;
    activity.signal(`onExtensionsSaved - ${JSON.stringify(adExtensionsAssociations)}`);
    return Promise.resolve(data);
  }, [activity]);

  return {
    load,
    save,
  };
};
